import {
    SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE,
    useRecognizeScheduleMilestonesBulkMutation,
} from "@api/scheduleMilestone.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { runBulkRequestGroups } from "./bulkRequestRunner";
import {
    formatMilestonesRecognizedBulkFailureToast,
    formatMilestonesRecognizedBulkToast,
} from "./milestoneAchievedCopy";
import {
    getMilestoneAchievedRowKey,
    MilestonePostingDates,
} from "./milestoneAchievedDates";
import { MilestoneAchievedBulkRow } from "./MilestoneAchievedRowsList";
import { buildBulkRecognizeRequests } from "./milestoneSelectionHelpers";

interface BulkSubmitTarget {
    rows: MilestoneAchievedBulkRow[];
    skippedCount: number;
}

/**
 * Posts every selected milestone, one entity-scoped request per entity.
 * Rows the server refused come back in `failed`, and a request that fails
 * outright counts each of its rows as failed; the toast tallies both and the
 * modal closes as soon as anything posted.
 */
export function useMilestoneAchievedBulkSubmit(options: {
    onClose: () => void;
    onCompleted?: () => void;
}): {
    submit: (options: {
        target: BulkSubmitTarget;
        postingDates: MilestonePostingDates;
    }) => Promise<void>;
    isPending: boolean;
} {
    const { onClose, onCompleted } = options;
    const { toast, error } = useToaster();
    const mutation = useRecognizeScheduleMilestonesBulkMutation();

    const submit = async ({
        target,
        postingDates,
    }: {
        target: BulkSubmitTarget;
        postingDates: MilestonePostingDates;
    }) => {
        const outcome = await runBulkRequestGroups({
            groups: buildBulkRecognizeRequests({
                rows: target.rows.map((row) => ({
                    ...row,
                    postingDate: postingDates[getMilestoneAchievedRowKey(row)],
                })),
            }),
            run: (request) => mutation.mutateAsync(request),
            countRows: (request) => request.milestones.length,
            fallbackMessage: SCHEDULE_MILESTONES_RECOGNIZE_BULK_FAILED_MESSAGE,
        });
        const recognized = outcome.results.flatMap(
            (result) => result.recognized,
        );
        const recognizedCount = recognized.length;
        const overRecognizedCount = recognized.filter(
            (row) => row.isOverRecognized,
        ).length;
        if (recognizedCount === 0) {
            error(
                formatMilestonesRecognizedBulkFailureToast(
                    outcome.failedMessages[0],
                ),
            );
            return;
        }
        toast(
            formatMilestonesRecognizedBulkToast({
                recognizedCount,
                failedCount: outcome.failedMessages.length,
                skippedCount: target.skippedCount,
                overRecognizedCount,
            }),
        );
        onCompleted?.();
        onClose();
    };

    return { submit, isPending: mutation.isPending };
}
